import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import Logo from '../../Images/gtrc_logo.webp';

let manualCount = 0;
let listeners: Array<() => void> = [];
const notify = () => listeners.forEach((fn) => fn());

export const globalLoaderStart = () => { manualCount++; notify(); };
export const globalLoaderEnd = () => { manualCount = Math.max(0, manualCount - 1); notify(); };

export const trackedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  globalLoaderStart();
  try {
    const res = await fetch(input, init);
    globalLoaderEnd();
    return res;
  } catch (e) {
    globalLoaderEnd();
    throw e;
  }
};

const SKIP_URLS = [
  '/email-hub/stats',
  '/email-hub/v3/notifications/unread-count',
  // Lightweight inline validation that runs while the user is typing —
  // must never raise the full-screen loading overlay.
  '/customer/check-duplicate-phone',
  // Background polling endpoints — these run on a timer (every 10-15s)
  // to detect new/changed leads without the operator's input, so they
  // must NEVER raise the full-screen overlay. Otherwise the page flashes
  // a loading spinner every interval and looks broken.
  '/leads/check-changes',
];

// RTK Query endpoint names whose pending state must NOT raise the global
// overlay. Same rationale as SKIP_URLS, for the Redux-store code path below.
const SKIP_ENDPOINTS = ['checkDuplicatePhone'];

const GlobalLoader: React.FC = () => {
  const [manualLoading, setManualLoading] = useState(false);
  const patchedRef = useRef(false);

  useEffect(() => {
    const handler = () => setManualLoading(manualCount > 0);
    listeners.push(handler);
    return () => { listeners = listeners.filter((fn) => fn !== handler); };
  }, []);

  useEffect(() => {
    if (patchedRef.current) return;
    patchedRef.current = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : (input as Request)?.url || '';
      const shouldSkip = SKIP_URLS.some((s) => url.includes(s));
      if (!shouldSkip) globalLoaderStart();
      return originalFetch(input, init)
        .then((res: Response) => { if (!shouldSkip) globalLoaderEnd(); return res; })
        .catch((err: any) => { if (!shouldSkip) globalLoaderEnd(); throw err; });
    }) as typeof fetch;

    const OrigXHROpen = XMLHttpRequest.prototype.open;
    const OrigXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL) {
      (this as any)._glSkip = SKIP_URLS.some((s) => (typeof url === 'string' ? url : url.toString()).includes(s));
      // eslint-disable-next-line prefer-rest-params
      return OrigXHROpen.apply(this, arguments as any);
    };

    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: any) {
      if (!(this as any)._glSkip) {
        globalLoaderStart();
        this.addEventListener('loadend', () => globalLoaderEnd(), { once: true });
      }
      return OrigXHRSend.call(this, body);
    };
  }, []);

  const queries = useSelector((state: any) => state.attoDeskApi?.queries ?? {});
  const mutations = useSelector((state: any) => state.attoDeskApi?.mutations ?? {});
  const hasPending = Object.values(queries).some(
      (q: any) => q?.status === 'pending' && !SKIP_ENDPOINTS.includes(q?.endpointName)
    )
    || Object.values(mutations).some((m: any) => m?.status === 'pending');

  const isLoading = hasPending || manualLoading;

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        pointerEvents: 'none',
      }}
    >
      <img
        src={Logo}
        alt="Global Tamil Research Conference"
        style={{ width: 'min(300px, 72vw)', height: 'auto', animation: 'glfade 1.8s ease-in-out infinite' }}
      />
      <Box
        sx={{
          position: 'relative',
          width: 'min(230px, 58vw)',
          height: 3,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#efe6e6',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '40%',
            borderRadius: 3,
            background: 'linear-gradient(90deg, #b01e28, #d4a03a)',
            animation: 'glslide 1.3s ease-in-out infinite',
          }}
        />
      </Box>
      <style>
        {`
          @keyframes glfade{0%,100%{opacity:1}50%{opacity:.55}}
          @keyframes glslide{0%{left:-40%}100%{left:100%}}
        `}
      </style>
    </Box>
  );
};

export default GlobalLoader;
